import React, { useEffect, useRef, useState } from "react";
import { Loader2, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { reApi, errMsg } from "@/lib/residentApi";
import { AnswerScores, ReviewCard, ReviewCounts, ReviewSummary } from "@/components/nds/windows/resident/reviewParts";

const WINDOWS = [3, 7, 14, 30];
const btn = "px-3 py-1.5 border rounded-sm font-mono text-[10px] tracking-[0.18em] uppercase transition-colors";

function Draft({ draft, onSaved, rid }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await reApi.post(`/residents/${rid}/reviews`, { ...draft, note }); toast.success("Review saved · touchpoint logged"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="review-draft" className="p-4 border border-nex-gold/40 rounded-sm space-y-3 bg-nex-gold/[0.03]">
      <ReviewSummary text={draft.summary} testid="review-draft-summary" />
      <ReviewCounts counts={draft.counts} />
      <AnswerScores scores={draft.answer_scores} testid="review-draft-scores" />
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Your note (optional) — what you discussed, next steps"
        data-testid="review-draft-note" className="w-full px-3 py-2 bg-white/[0.03] border border-white/10 rounded-sm text-sm text-nex-silver focus:outline-none focus:border-nex-gold/60" />
      <div className="flex justify-end">
        <button type="button" disabled={busy} onClick={save} data-testid="review-draft-save" className={`${btn} border-nex-gold text-nex-gold hover:bg-nex-gold/10`}>
          {busy ? "Saving…" : "Save as this week's touchpoint"}
        </button>
      </div>
    </div>
  );
}

export default function ReviewPanel({ resident, onClose, onSaved }) {
  const [days, setDays] = useState(7);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [past, setPast] = useState([]);
  const rid = resident.resident_id;
  const ref = useRef(null);
  useEffect(() => { ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, [rid]);
  const loadPast = () => reApi.get(`/residents/${rid}/reviews`).then((d) => setPast(d.reviews)).catch(() => setPast([]));
  useEffect(() => { loadPast(); setDraft(null); }, [rid]); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async () => {
    setBusy(true); setDraft(null);
    try {
      const { job_id: id } = await reApi.post(`/residents/${rid}/reviews/generate`, { days });
      for (let i = 0; i < 90; i += 1) {
        await new Promise((ok) => setTimeout(ok, 2000));
        const j = await reApi.get(`/review-jobs/${id}`);
        if (j.status === "done") { setDraft(j.result); return; }
        if (j.status === "error") throw new Error(j.error);
      }
      throw new Error("The review is taking too long — try again");
    } catch (e) { toast.error(e.response ? errMsg(e) : e.message); } finally { setBusy(false); }
  };
  return (
    <aside ref={ref} data-testid="review-panel" className="scroll-mt-4 border border-white/10 rounded-sm p-4 space-y-4 bg-black/20">
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-nex-muted">Weekly touchpoint review</div>
          <div data-testid="review-panel-name" className="text-nex-silver font-medium">{resident.name}</div>
        </div>
        <button type="button" onClick={onClose} data-testid="review-panel-close" aria-label="Close" className="p-1 text-nex-muted hover:text-nex-gold"><X className="h-4 w-4" /></button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[10px] text-nex-muted">Look back</span>
        {WINDOWS.map((d) => (
          <button key={d} type="button" onClick={() => setDays(d)} data-testid={`review-days-${d}`} aria-pressed={days === d}
            className={`${btn} ${days === d ? "border-nex-gold text-nex-gold bg-nex-gold/10" : "border-white/15 text-nex-muted hover:text-nex-silver"}`}>{d} days</button>
        ))}
        <button type="button" disabled={busy} onClick={run} data-testid="review-run" className={`${btn} ml-auto inline-flex items-center gap-1.5 border-nex-gold bg-nex-gold text-black hover:bg-nex-gold/90`}>
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} {busy ? "Reviewing…" : "Review"}
        </button>
      </div>
      {draft && <Draft draft={draft} rid={rid} onSaved={() => { setDraft(null); loadPast(); onSaved(); }} />}
      <div className="space-y-2">
        <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-nex-muted">Past reviews · {past.length}</div>
        {past.length ? <ul className="space-y-3 max-h-[420px] overflow-y-auto pr-1">{past.map((r) => <ReviewCard key={r.id} r={r} />)}</ul>
          : <p data-testid="review-panel-empty" className="text-xs text-nex-muted">No reviews yet.</p>}
      </div>
    </aside>
  );
}
